import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationsKeys } from '@/entities/conversations';
import { API_TAGS } from '@/shared/api';
import { fetchPhrases, updatePhraseText } from './api';
import type { PhraseListParams, PhraseListResponse, PhraseTextUpdate } from './types';

/** Ключи кэша перечня реплик */
export const phrasesKeys = {
  all: [API_TAGS.PHRASES] as const,
  lists: () => [...phrasesKeys.all, 'list'] as const,
  list: (params: PhraseListParams = {}) => [...phrasesKeys.lists(), { ...params }] as const,
};

/**
 * Страница перечня реплик раздела «Аудит аудио».
 * Прежняя страница остаётся на экране до прихода новой: при смене окна, отбора или порядка
 * таблица не мигает пустотой.
 */
export const usePhrases = (params: PhraseListParams) =>
  useQuery({
    queryKey: phrasesKeys.list(params),
    queryFn: () => fetchPhrases(params),
    placeholderData: keepPreviousData,
  });

/**
 * Правка текста реплики. По успеху строка перечня берёт текст и время обновления из ответа,
 * а сам перечень остаётся прежним: вычитка идёт по репликам подряд, и перезапрос на каждую
 * пройденную реплику переставлял бы строки под курсором — порядок по умолчанию ведёт как раз
 * по времени обновления. Правленый текст реплики показывает и транскрипт её диалога —
 * он перестаёт быть действующим.
 */
export const useUpdatePhraseText = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, text }: PhraseTextUpdate) => updatePhraseText(id, text),
    onSuccess: (phrase) => {
      queryClient.setQueriesData<PhraseListResponse>({ queryKey: phrasesKeys.lists() }, (previous) =>
        previous
          ? { ...previous, items: previous.items.map((item) => (item.id === phrase.id ? phrase : item)) }
          : previous,
      );
      queryClient.invalidateQueries({ queryKey: conversationsKeys.transcripts() });
    },
  });
};
